import React from "react";
import { graphql, useFragment } from "react-relay";

import { ReactComponent as LinearLogo } from "~/legacy-assets/images/logos/linear.svg";
import { promptLinearAuth } from "~/lib/auth";
import type { IntegrationConfigProps, IntegrationDetailsType } from "~/stream/StreamSettings/ActionItemIntegrations";

import type { ActionItemIntegration, BaseIntegrationDetails } from "../actionItemIntegration";
import { LinearCreateExternalTicketPane } from "./LinearCreateExternalTicketPane";
import { LinearImportIssue } from "./LinearImportIssue";
import { LinearStreamSettingsConfigView, LinearStreamSettingsIntegrationCard } from "./LinearStreamSettings";
import { LinearActionItemIntegrationLinkWidget } from "./Widget";

import type { linearStreamDetails$key } from "~/__generated__/linearStreamDetails.graphql";

import styles from "~/integrations/actionItemIntegration/css/styles.module.css";

graphql`
	fragment linearSpecificData on LinearActionItemIntegrationSpecificDataNode {
		__typename
		externalIdentifier
		teamName
		statusName
		statusColor
		linearUser {
			id
			isConnected
			organizationUrlKey
		}
	}
`;

const linearStreamDetails = graphql`
	fragment linearStreamDetails on LinearStreamIntegrationNode {
		linearUser {
			user {
				fullName
			}
		}
		teamName
		includeAiGenerated
		teamId
		projectId
		cycleId
		statusId
		uncheckedStatusId
		doneStatusId
	}
`;

export interface LinearStreamIntegrationDetails extends BaseIntegrationDetails {
	includeAiGenerated: boolean;
	teamId: string;
	projectId?: string;
	cycleId?: string;
	statusId?: string;
	uncheckedStatusId?: string;
	doneStatusId?: string;
}

function useLinearStreamIntegrationDetails(
	integrationOrAsanaStream: IntegrationDetailsType,
): LinearStreamIntegrationDetails {
	if (!("actionItemIntegration" in integrationOrAsanaStream) || !integrationOrAsanaStream.actionItemIntegration) {
		throw new Error("Stream integration details are for Asana, not Linear"); // Should never encounter this
	}

	const streamDetails = useFragment<linearStreamDetails$key>(
		linearStreamDetails,
		integrationOrAsanaStream.actionItemIntegration.integrationSpecificDetails,
	);

	return {
		containerTitle: streamDetails.teamName,
		createdBy: streamDetails.linearUser.user.fullName,
		includeAiGenerated: streamDetails.includeAiGenerated,
		teamId: streamDetails.teamId,
		projectId: streamDetails.projectId ?? undefined,
		cycleId: streamDetails.cycleId ?? undefined,
		statusId: streamDetails.statusId ?? undefined,
		uncheckedStatusId: streamDetails.uncheckedStatusId ?? undefined,
		doneStatusId: streamDetails.doneStatusId ?? undefined,
	};
}

function getLinearConfigurationPane({
	integration,
	stream,
	viewer,
	onFinish,
	onCancel,
	integrationDetails,
}: IntegrationConfigProps) {
	return (
		<LinearStreamSettingsConfigView
			integration={integration}
			stream={stream}
			viewer={viewer}
			onFinish={onFinish}
			onCancel={onCancel}
			integrationDetails={integrationDetails as LinearStreamIntegrationDetails}
		/>
	);
}

export const linear: ActionItemIntegration = {
	key: "LINEAR",
	logo: <LinearLogo className={styles.integrationLogo} />,
	billingFeatureName: "linear_integration",
	displayName: "Linear",
	externalActionItemName: "issue",
	externalContainerType: "",
	renderPill: LinearActionItemIntegrationLinkWidget,
	renderCreateNewExternalTicketPane: LinearCreateExternalTicketPane,
	renderImportExistingExternalTicketPane: LinearImportIssue,
	promptAuth: promptLinearAuth,
	streamSettingsCard: LinearStreamSettingsIntegrationCard,
	configurationPane: getLinearConfigurationPane,
	useIntegrationDetails: useLinearStreamIntegrationDetails,
};
